import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import {
  deleteSheet, getSheets, getVersionSets, updateSheet, bulkUpdateSheetTags,
  getSavedSearches, createSavedSearch, deleteSavedSearch,
} from '../utils/api';
import Toolbar    from '../components/Toolbar';
import PdfViewer  from '../components/PdfViewer';
import CompareViewer       from '../components/CompareViewer';
import CompareSetupModal   from '../components/CompareSetupModal';
import { dedupeLatestPerSheet } from '../utils/versionSets';
import DrawingsSettings from '../components/DrawingsSettings';
import VersionSetsModal from '../components/VersionSetsModal';
import ActivitiesModal  from '../components/ActivitiesModal';
import ShareModal       from '../components/ShareModal';
import SheetExportModal from '../components/SheetExportModal';
import styles from './ProjectDrawings.module.css';

// True when any of the search/filter inputs has a non-empty value. Used
// to decide whether to show the "Save current" affordance.
function hasActiveFilters({ localSearch, localDiscipline, filterVersionSet, localTag, timeMachineDate }) {
  return Boolean(
    (localSearch || '').trim() ||
    localDiscipline ||
    filterVersionSet ||
    localTag ||
    timeMachineDate
  );
}

// ISO-date helpers — version_sets.published_at and issuance_date come
// back as Date objects from the pg driver and as ISO strings on the
// wire; both shapes need to compare consistently.
function toIsoDate(v) {
  if (!v) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date)     return v.toISOString().slice(0, 10);
  return '';
}

// ─── Utility ────────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const sec  = Math.floor(diff / 1000);
  if (sec < 60)   return 'just now';
  const min = Math.floor(sec  / 60);
  if (min < 60)   return `${min}m ago`;
  const hr  = Math.floor(min  / 60);
  if (hr  < 24)   return `${hr}h ago`;
  const day = Math.floor(hr   / 24);
  if (day < 30)   return `${day}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function ProjectDrawings() {
  const {
    sheets, setSheets, disciplines, versionSets, role, project, currentUser,
    setVersionSets,
    setUploadModalOpen,
    selectedSheets, setSelectedSheets,
    activeSheet, setActiveSheet,
    recentSheetIds,
    drawingsViewMode: viewMode, setDrawingsViewMode: setViewMode,
    drawingsSettingsOpen, setDrawingsSettingsOpen,
    setVersionSetsModalOpen,
    compareConfig, setCompareSetupOpen,
    filterVersionSet, setFilterVersionSet,
  } = useStore();

  const [localSearch,     setLocalSearch]     = useState('');
  const [localDiscipline, setLocalDiscipline] = useState('');
  const [localTag,        setLocalTag]        = useState('');
  // Server-side content search isn't implemented yet — the checkbox is
  // wired through but currently has no effect on the filter. Kept so the
  // UX is in place when we add OCR-text indexing.
  const [searchContent,   setSearchContent]   = useState(false);

  // Saved searches (per-user, per-project). Loaded on mount; refreshed
  // after save/delete.
  const [saved, setSaved]               = useState([]);
  const [savingSearchOpen, setSavingSearchOpen] = useState(false);
  const [newSavedName,    setNewSavedName]      = useState('');

  // Modal state for the row menu actions.
  const [activitiesSheet, setActivitiesSheet] = useState(null);
  const [shareSheet,      setShareSheet]      = useState(null);
  const [exportSheet,     setExportSheet]     = useState(null);

  // Right-side filter sidebar — search/dropdowns/saved-searches all live
  // there now so the main header stays uncluttered. Closed by default;
  // header has a FILTERS button + a count badge when anything is active.
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);

  // Time Machine — picks a date and shows the drawings list as it existed
  // on that date. Used directly as a `published_at` cutoff in the filtering
  // pass below — no longer translates to a single VS id. Same dedupe rule
  // applies inside the cutoff window: latest issuance per sheet number.
  // Local-only state; resets on page reload / unmount, doesn't persist.
  const [timeMachineDate, setTimeMachineDate] = useState(''); // 'YYYY-MM-DD' when active

  function applyTimeMachine(dateStr) {
    if (!dateStr) return;
    setTimeMachineDate(dateStr);
  }
  function exitTimeMachine() {
    setTimeMachineDate('');
  }
  // True when the picked date falls before any VS was published — used to
  // surface a "no drawings as of this date" empty state.
  const timeMachineHasAnyEligibleVs = timeMachineDate
    ? versionSets.some((v) => v.is_published && toIsoDate(v.published_at) <= timeMachineDate)
    : false;

  // Pulls the user's saved searches for this project.
  async function refreshSavedSearches() {
    if (!project) return;
    try { setSaved(await getSavedSearches(project.id)); }
    catch (_) { /* non-fatal */ }
  }
  useEffect(() => { refreshSavedSearches(); /* eslint-disable-next-line */ }, [project?.id, currentUser?.id]);

  function applySavedSearch(s) {
    const p = s.params || {};
    setLocalSearch(p.search || '');
    setLocalDiscipline(p.discipline_id || '');
    setFilterVersionSet(p.version_set_id || '');
    setLocalTag(p.tag || '');
    setSearchContent(!!p.search_content);
  }
  function clearAllFilters() {
    setLocalSearch(''); setLocalDiscipline(''); setFilterVersionSet('');
    setLocalTag(''); setSearchContent(false);
    setTimeMachineDate('');
  }

  // Filters live for the duration of the visit to this page. When
  // ProjectDrawings unmounts (user navigates to Files / Forms / etc., or
  // closes the tab), wipe the store-backed filters so coming back lands
  // on the default "current view per sheet number" again. Local-state
  // filters (search/discipline/tag/timeMachine) reset automatically with
  // the component unmount.
  useEffect(() => () => {
    setFilterVersionSet('');
    // eslint-disable-next-line
  }, []);
  async function handleSaveSearch() {
    if (!newSavedName.trim()) return;
    try {
      await createSavedSearch({
        project_id: project.id,
        name: newSavedName.trim(),
        params: {
          search: localSearch,
          discipline_id: localDiscipline,
          version_set_id: filterVersionSet,
          tag: localTag,
          search_content: searchContent,
        },
      });
      toast.success(`Saved as "${newSavedName.trim()}"`);
      setNewSavedName('');
      setSavingSearchOpen(false);
      await refreshSavedSearches();
    } catch (err) { toast.error(err.message); }
  }
  async function handleDeleteSavedSearch(s) {
    try {
      await deleteSavedSearch(s.id);
      await refreshSavedSearches();
    } catch (err) { toast.error(err.message); }
  }

  // Inline sheet-row editing (title / discipline / tags only — version set is
  // upload-defined and stays read-only). One row at a time.
  const [editSheetId,   setEditSheetId]   = useState(null);
  const [editSheetForm, setEditSheetForm] = useState({ title: '', discipline_id: '', tags: '' });
  const [savingSheet,   setSavingSheet]   = useState(false);

  // Bulk tag editor (opened from the selection bar when multiple sheets are
  // selected). Add / remove are independent — leaving one blank is fine.
  const [bulkTagsOpen,   setBulkTagsOpen]   = useState(false);
  const [bulkAddTags,    setBulkAddTags]    = useState('');
  const [bulkRemoveTags, setBulkRemoveTags] = useState('');
  const [bulkTagsSaving, setBulkTagsSaving] = useState(false);

  async function applyBulkTags() {
    const add    = bulkAddTags.split(',').map((t) => t.trim()).filter(Boolean);
    const remove = bulkRemoveTags.split(',').map((t) => t.trim()).filter(Boolean);
    if (!add.length && !remove.length) {
      toast.error('Enter tags to add or remove');
      return;
    }
    setBulkTagsSaving(true);
    try {
      const ids = Array.from(selectedSheets);
      const { updated } = await bulkUpdateSheetTags(ids, add, remove);
      if (project) {
        const updatedSheets = await getSheets({ project_id: project.id });
        setSheets(updatedSheets);
      }
      setBulkTagsOpen(false);
      setBulkAddTags('');
      setBulkRemoveTags('');
      toast.success(`Updated tags on ${updated} sheet${updated !== 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err.message || 'Bulk tag update failed');
    } finally {
      setBulkTagsSaving(false);
    }
  }

  function startEditSheet(sheet) {
    setEditSheetId(sheet.id);
    setEditSheetForm({
      title: sheet.title || '',
      discipline_id: sheet.discipline_id || '',
      tags: Array.isArray(sheet.tags) ? sheet.tags.join(', ') : '',
    });
  }
  function cancelEditSheet() {
    setEditSheetId(null);
  }
  async function saveEditSheet(sheetId) {
    setSavingSheet(true);
    try {
      const tagsArr = editSheetForm.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const payload = {
        title: editSheetForm.title.trim(),
        // Empty string in the select = "no discipline" → send null to clear.
        discipline_id: editSheetForm.discipline_id || null,
        tags: tagsArr,
      };
      await updateSheet(sheetId, payload);
      // Refetch so derived fields (discipline_name / discipline_designator /
      // markup_count / etc.) come from the server join, not guessed locally.
      if (project) {
        const updatedSheets = await getSheets({ project_id: project.id });
        setSheets(updatedSheets);
      }
      setEditSheetId(null);
      toast.success('Sheet updated');
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setSavingSheet(false);
    }
  }

  const canEdit = role === 'admin' || role === 'pm';

  // Tag dropdown options — every unique tag across all loaded sheets.
  const allTags = useMemo(() => {
    const set = new Set();
    for (const s of sheets) {
      for (const t of (s.tags || [])) set.add(t);
    }
    return Array.from(set).sort();
  }, [sheets]);

  // ── Client-side filtering ──────────────────────────────────────────────────
  // Search matches against sheet_number, title, version-set name, and the
  // discipline name/designator. searchContent is the placeholder for a
  // future server-side OCR-text search (currently a no-op).
  //
  // Dedupe rule: when no specific version set is selected (the default),
  // collapse the list to one row per sheet_number — the latest version
  // by issuance_date. Picking a VS in the dropdown opts out of the dedupe
  // and shows every sheet in that issuance, including older revisions of
  // a sheet number that also exists in newer sets.
  //
  // Time Machine: when timeMachineDate is set, we pre-narrow to sheets
  // whose VS was published_at <= cutoff before applying the dedupe, so
  // the user sees the project as it stood on that date.
  const filtered = useMemo(() => {
    const q = localSearch.trim().toLowerCase();
    const passesText = (s) => {
      if (localDiscipline && s.discipline_id !== localDiscipline) return false;
      if (filterVersionSet && s.version_set_id !== filterVersionSet) return false;
      if (localTag && !(s.tags || []).includes(localTag)) return false;
      if (timeMachineDate) {
        // Only sheets whose VS was published by the cutoff date count
        // as "existing on" that date. Sheets without a VS, or in a VS
        // that hadn't been published yet, are excluded.
        const pub = toIsoDate(s.published_at);
        if (!pub || pub > timeMachineDate) return false;
      }
      if (q) {
        const hay = [
          s.sheet_number,
          s.title,
          s.version_set_name,
          s.discipline_name,
          s.discipline_designator,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    };
    const passed = sheets.filter(passesText);
    // No VS picked → dedupe to the latest version of each sheet number.
    if (!filterVersionSet) return dedupeLatestPerSheet(passed);
    return passed;
  }, [sheets, localSearch, localDiscipline, filterVersionSet, localTag, timeMachineDate]);

  // ── Viewer prev / next ─────────────────────────────────────────────────────
  const currentIndex = useMemo(
    () => filtered.findIndex((s) => s.id === activeSheet?.id),
    [filtered, activeSheet]
  );
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < filtered.length - 1;

  function openSheet(sheet) {
    setActiveSheet(sheet);
    setViewMode('viewer');
  }
  function goToList() {
    setViewMode('list');
  }
  function goPrev() { if (hasPrev) setActiveSheet(filtered[currentIndex - 1]); }
  function goNext() { if (hasNext) setActiveSheet(filtered[currentIndex + 1]); }

  // Keyboard nav while in viewer
  const handleKey = useCallback((e) => {
    if (viewMode !== 'viewer') return;
    if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); goPrev(); }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown')  { e.preventDefault(); goNext(); }
    if (e.key === 'Escape')                               { goToList(); }
  }, [viewMode, currentIndex, filtered]); // eslint-disable-line

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  // ── Selection helpers ──────────────────────────────────────────────────────
  const allSelected  = filtered.length > 0 && filtered.every((s) => selectedSheets.has(s.id));
  const someSelected = !allSelected && filtered.some((s) => selectedSheets.has(s.id));

  function toggleAll() {
    setSelectedSheets(allSelected ? new Set() : new Set(filtered.map((s) => s.id)));
  }
  function toggleOne(id) {
    const next = new Set(selectedSheets);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedSheets(next);
  }

  // ── Delete (soft) ──────────────────────────────────────────────────────────
  async function handleDeleteOne(sheet) {
    const label = sheet.sheet_number || sheet.title || sheet.filename;
    if (!window.confirm(`Move "${label}" to Deleted?`)) return;
    try {
      await deleteSheet(sheet.id);
      toast.success('Sheet deleted');
      await refreshAfterDelete();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  }
  async function handleDeleteSelected() {
    const ids = Array.from(selectedSheets);
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} sheet${ids.length !== 1 ? 's' : ''} to Deleted?`)) return;
    try {
      await Promise.all(ids.map((id) => deleteSheet(id)));
      toast.success(`${ids.length} sheet${ids.length !== 1 ? 's' : ''} deleted`);
      setSelectedSheets(new Set());
      await refreshAfterDelete();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  }
  async function refreshAfterDelete() {
    if (!project) return;
    const [updatedSheets, updatedVs] = await Promise.all([
      getSheets({ project_id: project.id }),
      getVersionSets(project.id),
    ]);
    setSheets(updatedSheets);
    setVersionSets(updatedVs);
  }

  // ── Viewer mode ────────────────────────────────────────────────────────────
  if (viewMode === 'viewer' && activeSheet) {
    return (
      <div className={styles.page}>
        {/* Viewer top bar */}
        <div className={styles.viewerBar}>
          <div className={styles.viewerInfo}>
            <span className={styles.viewerNum}>{activeSheet.sheet_number}</span>
            {activeSheet.title && activeSheet.title !== activeSheet.sheet_number && (
              <span className={styles.viewerTitle}>{activeSheet.title}</span>
            )}
            {activeSheet.version_set_name && (
              <span className={styles.viewerChip}>{activeSheet.version_set_name}</span>
            )}
            {activeSheet.discipline_name && (
              <span className={styles.viewerChip}>
                {activeSheet.discipline_designator
                  ? `${activeSheet.discipline_designator} · ${activeSheet.discipline_name}`
                  : activeSheet.discipline_name}
              </span>
            )}
          </div>

          <div className={styles.spacer} />

          {/* Prev / Next navigation + recents dropdown */}
          <div className={styles.viewerNav}>
            <button
              className={styles.navBtn}
              onClick={() => setCompareSetupOpen(true)}
              disabled={!!compareConfig}
              title="Compare this sheet against another issuance"
              style={{ width: 'auto', padding: '0 10px', fontSize: '10px', letterSpacing: '0.08em' }}
            >
              COMPARE ⇆
            </button>
            <RecentSheetsDropdown
              recentSheetIds={recentSheetIds}
              sheets={sheets}
              activeSheetId={activeSheet?.id}
              onPick={(sheet) => setActiveSheet(sheet)}
            />
            <button
              className={styles.navBtn}
              onClick={goPrev}
              disabled={!hasPrev || !!compareConfig}
              title={compareConfig ? 'Exit compare to navigate sheets' : 'Previous sheet (←)'}
            >
              <ChevronLeftIcon />
            </button>
            <span className={styles.navCount}>
              {currentIndex + 1} / {filtered.length}
            </span>
            <button
              className={styles.navBtn}
              onClick={goNext}
              disabled={!hasNext || !!compareConfig}
              title={compareConfig ? 'Exit compare to navigate sheets' : 'Next sheet (→)'}
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>

        {/* PDF canvas + vertical toolbar — Compare mode swaps in
            CompareViewer and hides the markup toolbar (only measurement
            tools will eventually live there in a follow-up commit). */}
        <div className={styles.viewerBody}>
          <div className={styles.viewerContent}>
            {compareConfig ? <CompareViewer /> : <PdfViewer />}
          </div>
          {!compareConfig && <Toolbar />}
        </div>

        <CompareSetupModal />
      </div>
    );
  }

  // ── List mode ──────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>DRAWINGS</h1>
          {canEdit && (
            <button className={styles.addBtn} onClick={() => setUploadModalOpen(true)}>
              <PlusIcon /> ADD SHEETS
            </button>
          )}
        </div>
        <div className={styles.headerRight}>
          <button
            className={`${styles.headerBtn} ${filterSidebarOpen ? styles.headerBtnActive : ''} ${timeMachineDate ? styles.headerBtnActive : ''}`}
            onClick={() => setFilterSidebarOpen((o) => !o)}
            title="Search, filter, and Time Machine"
          >
            <FilterIcon /> FILTERS
            {(() => {
              const n = [
                localSearch.trim(),
                localDiscipline,
                localTag,
                !!filterVersionSet,
                !!timeMachineDate,
              ].filter(Boolean).length;
              return n > 0 ? <span className={styles.filterCountBadge}>{n}</span> : null;
            })()}
          </button>
          <button
            className={styles.headerBtn}
            onClick={() => setVersionSetsModalOpen(true)}
            title="Publish log and deleted sheets"
          >
            <LogIcon /> VERSION SETS
          </button>
          {canEdit && (
            <button className={styles.headerBtn} onClick={() => setDrawingsSettingsOpen(true)}>
              <GearIcon /> SETTINGS
            </button>
          )}
        </div>
      </div>

      {/* Time Machine banner — visible whenever a historical date is set,
          so the user always knows they're looking at a frozen-in-time view
          and not the live drawings list. */}
      {timeMachineDate && (
        <div className={styles.timeMachineBanner}>
          <ClockIcon />
          <span>
            <strong>Time Machine:</strong> showing drawings as of {timeMachineDate}
            {!timeMachineHasAnyEligibleVs && (
              <em className={styles.timeMachineBannerNote}>
                {' '}— no version sets had been issued by this date
              </em>
            )}
          </span>
          <button
            type="button"
            className={styles.timeMachineBannerExit}
            onClick={exitTimeMachine}
          >
            EXIT
          </button>
        </div>
      )}

      {/* Sheet count summary — always visible just below the header so
          users can see the table size without opening the sidebar. */}
      <div className={styles.subHeader}>
        <span className={styles.filterCount}>
          {filtered.length !== sheets.length
            ? `${filtered.length} of ${sheets.length}`
            : sheets.length}{' '}
          sheet{sheets.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Filter sidebar — right-side panel that slides in/out via the
          FILTERS header button. All search/filter/saved-search controls
          live here now so the page chrome stays minimal when the user
          isn't actively filtering. */}
      <aside
        className={`${styles.filterSidebar} ${filterSidebarOpen ? styles.filterSidebarOpen : ''}`}
        aria-hidden={!filterSidebarOpen}
      >
        <div className={styles.filterSidebarHeader}>
          <span>FILTERS</span>
          <button
            className={styles.filterSidebarClose}
            onClick={(e) => {
              // Blur first so focus isn't trapped inside an aria-hidden
              // ancestor when the sidebar slides out — Chrome warns about
              // it otherwise.
              e.currentTarget.blur();
              setFilterSidebarOpen(false);
            }}
            aria-label="Close filters"
          >
            ×
          </button>
        </div>
        <div className={styles.filterSidebarBody}>
          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Search</label>
            <input
              className={styles.searchInput}
              placeholder="Number, title, discipline, version set…"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
            <label
              className={styles.contentCheck}
              title="Also search the OCR'd contents of each drawing (server-side index — coming soon)."
            >
              <input
                type="checkbox"
                checked={searchContent}
                onChange={(e) => setSearchContent(e.target.checked)}
              />
              <span>Search contents</span>
            </label>
          </div>

          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Version set</label>
            <select
              className={styles.filterSelect}
              value={filterVersionSet}
              onChange={(e) => setFilterVersionSet(e.target.value)}
            >
              <option value="">Latest of each (default)</option>
              {(versionSets || []).map((vs) => (
                <option key={vs.id} value={vs.id}>{vs.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Discipline</label>
            <select
              className={styles.filterSelect}
              value={localDiscipline}
              onChange={(e) => setLocalDiscipline(e.target.value)}
            >
              <option value="">All disciplines</option>
              {disciplines.map((d) => (
                <option key={d.id} value={d.id}>{d.designator} — {d.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Tag</label>
            <select
              className={styles.filterSelect}
              value={localTag}
              onChange={(e) => setLocalTag(e.target.value)}
            >
              <option value="">All tags</option>
              {allTags.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Time Machine — moved out of the header into the filter sidebar
              since it's just another way to slice the drawings list. The
              banner across the top of the table still shows the active
              date so users always know they're looking at a frozen view. */}
          <div className={styles.filterField}>
            <label className={styles.filterLabel}>Time Machine</label>
            <input
              type="date"
              className={styles.filterSelect}
              value={timeMachineDate || ''}
              onChange={(e) => {
                if (e.target.value) applyTimeMachine(e.target.value);
                else exitTimeMachine();
              }}
            />
            {timeMachineDate && (
              <button
                type="button"
                className={styles.savedAddBtn}
                onClick={exitTimeMachine}
                title="Restore the live drawings view"
              >
                Today
              </button>
            )}
          </div>

          {/* Saved searches strip — moved into the sidebar so it sits next
              to the controls that produce a saved search. */}
          {(saved.length > 0 || hasActiveFilters({ localSearch, localDiscipline, filterVersionSet, localTag, timeMachineDate })) && (
            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Saved</label>
              <div className={styles.savedListVertical}>
                {saved.map((s) => (
                  <span key={s.id} className={styles.savedChip}>
                    <button
                      className={styles.savedChipMain}
                      onClick={() => applySavedSearch(s)}
                      title="Apply this saved search"
                    >{s.name}</button>
                    <button
                      className={styles.savedChipX}
                      onClick={() => handleDeleteSavedSearch(s)}
                      title="Delete saved search"
                    >×</button>
                  </span>
                ))}
              </div>
              {hasActiveFilters({ localSearch, localDiscipline, filterVersionSet, localTag, timeMachineDate }) && !savingSearchOpen && (
                <button className={styles.savedAddBtn} onClick={() => setSavingSearchOpen(true)}>
                  + Save current
                </button>
              )}
              {savingSearchOpen && (
                <div className={styles.savedNewWrap}>
                  <input
                    className={styles.savedNewInput}
                    placeholder="Name this search…"
                    autoFocus
                    value={newSavedName}
                    onChange={(e) => setNewSavedName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleSaveSearch(); }
                      if (e.key === 'Escape') { setSavingSearchOpen(false); setNewSavedName(''); }
                    }}
                  />
                  <div className={styles.savedNewButtonRow}>
                    <button className={styles.savedSaveBtn} onClick={handleSaveSearch}>Save</button>
                    <button className={styles.savedCancelBtn} onClick={() => { setSavingSearchOpen(false); setNewSavedName(''); }}>Cancel</button>
                  </div>
                </div>
              )}
              {hasActiveFilters({ localSearch, localDiscipline, filterVersionSet, localTag, timeMachineDate }) && (
                <button className={styles.savedAddBtn} onClick={clearAllFilters}>
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Content */}
      {sheets.length === 0 ? (
        <EmptyState canEdit={canEdit} onAdd={() => setUploadModalOpen(true)} />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkCol}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                  />
                </th>
                <th className={styles.numCol}>NUMBER</th>
                <th>VERSION SET</th>
                <th>DISCIPLINE</th>
                <th>TAGS</th>
                <th>UPDATED BY</th>
                <th className={styles.actionsCol}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.noResults}>
                    No sheets match your filters
                  </td>
                </tr>
              ) : (
                filtered.map((sheet) => (
                  <SheetRow
                    key={sheet.id}
                    sheet={sheet}
                    disciplines={disciplines}
                    selected={selectedSheets.has(sheet.id)}
                    canEdit={canEdit}
                    isEditing={editSheetId === sheet.id}
                    editForm={editSheetForm}
                    setEditForm={setEditSheetForm}
                    saving={savingSheet}
                    onToggle={() => toggleOne(sheet.id)}
                    onOpen={() => openSheet(sheet)}
                    onDelete={() => handleDeleteOne(sheet)}
                    onStartEdit={() => startEditSheet(sheet)}
                    onCancelEdit={cancelEditSheet}
                    onSaveEdit={() => saveEditSheet(sheet.id)}
                    onPickVersionSet={(id) => setFilterVersionSet(id || '')}
                    onPickDiscipline={(id) => setLocalDiscipline(id || '')}
                    onPickTag={(t) => setLocalTag(t || '')}
                    onShare={() => setShareSheet(sheet)}
                    onExport={() => setExportSheet(sheet)}
                    onActivities={() => setActivitiesSheet(sheet)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Selection bar */}
      {selectedSheets.size > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectionCount}>
            {selectedSheets.size} sheet{selectedSheets.size !== 1 ? 's' : ''} selected
          </span>
          <button className={styles.exportBtn}>
            <ExportIcon /> EXPORT PDF
          </button>
          {canEdit && (
            <button className={styles.bulkTagsBtn} onClick={() => setBulkTagsOpen(true)}>
              <TagIcon /> TAGS
            </button>
          )}
          {canEdit && (
            <button className={styles.deleteBtn} onClick={handleDeleteSelected}>
              <TrashIcon /> DELETE
            </button>
          )}
          <button className={styles.clearBtn} onClick={() => setSelectedSheets(new Set())}>
            ✕
          </button>
        </div>
      )}

      {/* Bulk tags editor */}
      {bulkTagsOpen && (
        <BulkTagsModal
          count={selectedSheets.size}
          addValue={bulkAddTags}
          removeValue={bulkRemoveTags}
          onAddChange={setBulkAddTags}
          onRemoveChange={setBulkRemoveTags}
          saving={bulkTagsSaving}
          onApply={applyBulkTags}
          onClose={() => { setBulkTagsOpen(false); setBulkAddTags(''); setBulkRemoveTags(''); }}
        />
      )}

      {/* Modals */}
      {drawingsSettingsOpen && (
        <DrawingsSettings onClose={() => setDrawingsSettingsOpen(false)} />
      )}
      <VersionSetsModal />{/* renders nothing when versionSetsModalOpen=false */}

      {activitiesSheet && (
        <ActivitiesModal
          sheet={activitiesSheet}
          onClose={() => setActivitiesSheet(null)}
        />
      )}
      {shareSheet && (
        <ShareModal
          sheet={shareSheet}
          project={project}
          onClose={() => setShareSheet(null)}
        />
      )}
      {exportSheet && (
        <SheetExportModal
          sheet={exportSheet}
          onClose={() => setExportSheet(null)}
        />
      )}
    </div>
  );
}

// ─── Sheet row ───────────────────────────────────────────────────────────────
function SheetRow({
  sheet, disciplines, selected, canEdit,
  isEditing, editForm, setEditForm, saving,
  onToggle, onOpen, onDelete,
  onStartEdit, onCancelEdit, onSaveEdit,
  onPickVersionSet, onPickDiscipline, onPickTag,
  onShare, onExport, onActivities,
}) {
  const tags = Array.isArray(sheet.tags) ? sheet.tags : [];

  // Edit mode — inline form for title / discipline / tags. Version set stays
  // read-only (upload-defined). Row click does NOT open the sheet.
  if (isEditing) {
    return (
      <tr
        className={`${styles.sheetRow} ${styles.rowEditing}`}
        onClick={(e) => e.stopPropagation()}
      >
        <td className={styles.checkCol} onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={selected} onChange={onToggle} />
        </td>
        <td>
          <div className={styles.sheetNum}>{sheet.sheet_number}</div>
          <input
            className={styles.sheetEditInput}
            value={editForm.title}
            placeholder="Sheet title"
            autoFocus
            onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  { e.preventDefault(); onSaveEdit(); }
              if (e.key === 'Escape') { e.preventDefault(); onCancelEdit(); }
            }}
          />
        </td>
        <td>
          {/* Version set is not editable here — upload-defined. */}
          {sheet.version_set_name ? (
            <div className={styles.versionCell}>
              <span className={styles.versionChip}>{sheet.version_set_name}</span>
              {sheet.is_published === false && (
                <span className={styles.draftPill}>DRAFT</span>
              )}
            </div>
          ) : (
            <span className={styles.cellEmpty}>—</span>
          )}
        </td>
        <td>
          <select
            className={styles.sheetEditSelect}
            value={editForm.discipline_id || ''}
            onChange={(e) => setEditForm((f) => ({ ...f, discipline_id: e.target.value }))}
          >
            <option value="">— Unassigned —</option>
            {(disciplines || []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.designator} · {d.name}
              </option>
            ))}
          </select>
        </td>
        <td>
          <input
            className={styles.sheetEditInput}
            value={editForm.tags}
            placeholder="tag1, tag2"
            onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  { e.preventDefault(); onSaveEdit(); }
              if (e.key === 'Escape') { e.preventDefault(); onCancelEdit(); }
            }}
          />
        </td>
        <td>
          <div className={styles.updatedName}>{sheet.uploaded_by || '—'}</div>
          <div className={styles.updatedTime}>{timeAgo(sheet.updated_at || sheet.created_at)}</div>
        </td>
        <td className={styles.actionsCol} onClick={(e) => e.stopPropagation()}>
          <div className={styles.rowActionsEdit}>
            <button className={styles.rowSaveBtn} onClick={onSaveEdit} disabled={saving}>
              {saving ? '…' : 'Save'}
            </button>
            <button className={styles.rowCancelBtn} onClick={onCancelEdit} disabled={saving}>
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr
      className={`${styles.sheetRow} ${selected ? styles.rowSelected : ''}`}
      onClick={onOpen}
    >
      <td className={styles.checkCol} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onToggle} />
      </td>
      <td>
        <div className={styles.sheetNum}>{sheet.sheet_number}</div>
        {sheet.title && sheet.title !== sheet.sheet_number && (
          <div className={styles.sheetTitle}>{sheet.title}</div>
        )}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {sheet.version_set_name ? (
          <div className={styles.versionCell}>
            <button
              type="button"
              className={`${styles.versionChip} ${styles.chipLink}`}
              onClick={() => onPickVersionSet?.(sheet.version_set_id)}
              title="Filter to this version set"
            >{sheet.version_set_name}</button>
            {sheet.is_published === false && (
              <span className={styles.draftPill} title="Draft — not yet published. Field users don't see this sheet.">
                DRAFT
              </span>
            )}
          </div>
        ) : (
          <span className={styles.cellEmpty}>—</span>
        )}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {sheet.discipline_name ? (
          <button
            type="button"
            className={`${styles.disciplineCell} ${styles.chipLink}`}
            onClick={() => onPickDiscipline?.(sheet.discipline_id)}
            title="Filter to this discipline"
          >
            {sheet.discipline_designator && (
              <span className={styles.disciplineBadge}>{sheet.discipline_designator}</span>
            )}
            {sheet.discipline_name}
          </button>
        ) : (
          <span className={styles.cellEmpty}>—</span>
        )}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {tags.length > 0 ? (
          <div className={styles.tagsCell}>
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                className={`${styles.tag} ${styles.chipLink}`}
                onClick={() => onPickTag?.(t)}
                title={`Filter to "${t}"`}
              >{t}</button>
            ))}
          </div>
        ) : (
          <span className={styles.cellEmpty}>—</span>
        )}
      </td>
      <td>
        <div className={styles.updatedName}>{sheet.uploaded_by || '—'}</div>
        <div className={styles.updatedTime}>{timeAgo(sheet.updated_at || sheet.created_at)}</div>
      </td>
      <td className={styles.actionsCol} onClick={(e) => e.stopPropagation()}>
        <SheetRowMenu
          canEdit={canEdit}
          onEdit={onStartEdit}
          onExport={onExport}
          onShare={onShare}
          onActivities={onActivities}
          onDelete={onDelete}
        />
      </td>
    </tr>
  );
}

// ─── Sheet row menu (3-dot) ─────────────────────────────────────────────────
// Replaces the per-row pencil + trash buttons with a single menu that
// covers the full action set: Edit, Export, Share, Activities, Delete.
function SheetRowMenu({ canEdit, onEdit, onExport, onShare, onActivities, onDelete }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  function fire(fn) {
    setOpen(false);
    fn?.();
  }

  return (
    <div ref={wrapRef} className={styles.rowMenuWrap}>
      <button
        type="button"
        className={styles.rowMenuTrigger}
        onClick={() => setOpen((v) => !v)}
        title="Row actions"
        aria-expanded={open}
      >
        <DotsIcon />
      </button>
      {open && (
        <div className={styles.rowMenuPopup} role="menu">
          {canEdit && (
            <button className={styles.rowMenuItem} onClick={() => fire(onEdit)}>
              <PencilIcon /> Edit
            </button>
          )}
          <button className={styles.rowMenuItem} onClick={() => fire(onExport)}>
            <DownloadIconRow /> Export PDF…
          </button>
          {canEdit && (
            <button className={styles.rowMenuItem} onClick={() => fire(onShare)}>
              <LinkIconRow /> Share…
            </button>
          )}
          <button className={styles.rowMenuItem} onClick={() => fire(onActivities)}>
            <ClockIcon /> View Activities
          </button>
          {canEdit && (
            <>
              <div className={styles.rowMenuSep} />
              <button className={`${styles.rowMenuItem} ${styles.rowMenuDanger}`} onClick={() => fire(onDelete)}>
                <TrashIcon /> Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Icons used by SheetRowMenu (above) and elsewhere. ClockIcon is also
// declared once more lower in the file; we keep this copy and remove the
// duplicate to fix the parse error from the earlier accidental double-add.
const DotsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="3" cy="8" r="1.4" fill="currentColor"/>
    <circle cx="8" cy="8" r="1.4" fill="currentColor"/>
    <circle cx="13" cy="8" r="1.4" fill="currentColor"/>
  </svg>
);
const DownloadIconRow = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 7l3 3 3-3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const LinkIconRow = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M7 9l-2.5 2.5a2.5 2.5 0 11-3.5-3.5L3.5 5.5"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 7l2.5-2.5a2.5 2.5 0 113.5 3.5L12.5 10.5"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M5.5 10.5l5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M8 5v3l2 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const FilterIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M2 3h12l-4.5 6v4l-3-1.5V9L2 3z"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Bulk tags modal ─────────────────────────────────────────────────────────
// Small centered modal for editing tags across the current selection.
// Add / remove are independent text fields (comma-separated). Apply runs
// one transactional request on the server.
function BulkTagsModal({ count, addValue, removeValue, onAddChange, onRemoveChange, saving, onApply, onClose }) {
  const addRef = useRef(null);
  useEffect(() => { addRef.current?.focus(); }, []);

  function onKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onApply(); }
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  }

  return (
    <div className={styles.bulkTagsBackdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.bulkTagsModal}>
        <div className={styles.bulkTagsHeader}>
          <span className={styles.bulkTagsTitle}>
            EDIT TAGS ON {count} SHEET{count !== 1 ? 'S' : ''}
          </span>
          <button className={styles.bulkTagsClose} onClick={onClose} disabled={saving}>×</button>
        </div>
        <div className={styles.bulkTagsBody}>
          <label className={styles.bulkTagsLabel}>ADD</label>
          <input
            ref={addRef}
            className={styles.bulkTagsInput}
            value={addValue}
            onChange={(e) => onAddChange(e.target.value)}
            onKeyDown={onKey}
            placeholder="tag1, tag2"
            disabled={saving}
          />
          <p className={styles.bulkTagsHint}>Appended to existing tags on each sheet; duplicates are ignored.</p>

          <label className={styles.bulkTagsLabel} style={{ marginTop: 12 }}>REMOVE</label>
          <input
            className={styles.bulkTagsInput}
            value={removeValue}
            onChange={(e) => onRemoveChange(e.target.value)}
            onKeyDown={onKey}
            placeholder="tag3, tag4"
            disabled={saving}
          />
          <p className={styles.bulkTagsHint}>Any of these tags present on a selected sheet get dropped.</p>
        </div>
        <div className={styles.bulkTagsActions}>
          <button className={styles.bulkTagsCancel} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.bulkTagsApply} onClick={onApply} disabled={saving}>
            {saving ? '…' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Recently viewed dropdown ────────────────────────────────────────────────
// Resolves `recentSheetIds` to current sheet objects (skipping any that are no
// longer in the loaded list — e.g. deleted or from another project) and shows
// the last 5 MRU in a dropdown next to the prev/next arrows.
function RecentSheetsDropdown({ recentSheetIds, sheets, activeSheetId, onPick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Outside-click to close.
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const items = useMemo(() => {
    const byId = new Map(sheets.map((s) => [s.id, s]));
    return (recentSheetIds || [])
      .map((id) => byId.get(id))
      .filter(Boolean);
  }, [recentSheetIds, sheets]);

  const disabled = items.length === 0;

  return (
    <div className={styles.recentWrap} ref={wrapRef}>
      <button
        className={styles.recentBtn}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        disabled={disabled}
        title="Recently viewed sheets"
      >
        <ClockIcon />
        <span>RECENT</span>
        <CaretIcon />
      </button>

      {open && (
        <div className={styles.recentMenu}>
          <div className={styles.recentMenuTitle}>RECENTLY VIEWED</div>
          {items.map((sheet) => {
            const isCurrent = sheet.id === activeSheetId;
            return (
              <button
                key={sheet.id}
                className={`${styles.recentItem} ${isCurrent ? styles.recentItemActive : ''}`}
                onClick={() => {
                  if (!isCurrent) onPick(sheet);
                  setOpen(false);
                }}
                disabled={isCurrent}
              >
                <span className={styles.recentItemNum}>{sheet.sheet_number}</span>
                {sheet.title && sheet.title !== sheet.sheet_number && (
                  <span className={styles.recentItemTitle}>{sheet.title}</span>
                )}
                {isCurrent && <span className={styles.recentItemBadge}>CURRENT</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────
function EmptyState({ canEdit, onAdd }) {
  return (
    <div className={styles.empty}>
      <EmptyIcon />
      <p className={styles.emptyTitle}>No drawings yet</p>
      {canEdit && (
        <>
          <p className={styles.emptyHint}>Upload PDFs to add drawings to this project</p>
          <button className={styles.emptyAddBtn} onClick={onAdd}>
            <PlusIcon /> ADD SHEETS
          </button>
        </>
      )}
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4l.75 8.5a1 1 0 001 .9h2.5a1 1 0 001-.9L11 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PencilIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <path d="M11.5 2.5a1.5 1.5 0 012 2L5.5 12.5 2 13l.5-3.5L11.5 2.5z"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const TagIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <path d="M2 2h6l6 6-6 6-6-6V2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <circle cx="5" cy="5" r="1" fill="currentColor"/>
  </svg>
);
const LogIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M5 6h6M5 9h4M5 12h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const GearIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M8 2v1.5M8 12.5V14M2 8h1.5M12.5 8H14M3.51 3.51l1.06 1.06M11.43 11.43l1.06 1.06M3.51 12.49l1.06-1.06M11.43 4.57l1.06-1.06"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const ExportIcon = () => (
  <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
    <path d="M8 2v9M5 8l3 3 3-3M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M6.5 2L3 5l3.5 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M3.5 2L7 5l-3.5 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
// (ClockIcon defined once near the SheetRowMenu icons block above.)
const CaretIcon = () => (
  <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
    <path d="M2 3.5L5 6.5l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const EmptyIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <rect x="8" y="6" width="28" height="36" rx="2" stroke="var(--text-faint)" strokeWidth="1.5"/>
    <path d="M14 16h20M14 22h20M14 28h12" stroke="var(--text-faint)" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="36" cy="36" r="9" fill="var(--surface)" stroke="var(--border2)" strokeWidth="1.5"/>
    <path d="M33 36h6M36 33v6" stroke="var(--text-faint)" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
